import { useSelector, useDispatch } from 'react-redux';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, ChevronLeft, ShoppingCart, FileText, Users, Settings, LogOut, Loader2 } from 'lucide-react';
import axios from 'axios';
import { resetLoginData } from '../../redux/action';
import { API_BASE_URL } from '../../App';
import cogentLogo from '../../assets/cogent-logo.jpeg';

/** Sidebar / drawer brand mark: padded container + object-contain (avoids “double box” on light logos). */
function SidebarBrandLogo({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const box =
    size === "sm"
      ? "h-8 w-8 p-1"
      : size === "lg"
        ? "h-12 w-12 p-1.5"
        : "h-10 w-10 p-1.5";
  return (
    <div
      className={`relative flex shrink-0 items-center justify-center rounded-2xl bg-white/[0.06] shadow-md ring-1 ring-white/12 ${box}`}
    >
      <img
        src={cogentLogo}
        alt="Cogent Tech Solutions"
        className="pointer-events-none max-h-full max-w-full object-contain object-center select-none"
        draggable={false}
      />
    </div>
  );
}

export const PanelPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role = "", authToken = "", id = "", username = "" } = useSelector((state: any) => state.loginData || {});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const location = useLocation();
  const routeTitles: Record<string, string> = {
    '/panel/orders': 'Quotations',
    '/panel/quotation-status': 'Quotation Status',
    '/panel/products/list': 'Products',
    '/panel/brands': 'Brands',
    '/panel/customers': 'Customers',
    '/panel/users': 'Users',
    '/panel/signatories': 'Signatories',
    '/panel/settings': 'Settings',
  };
  const currentTitle = routeTitles[location.pathname] || 'Dashboard';

  const isActive = (path: string) => location.pathname === path;
  const isDropdownActive = (paths: string[]) => paths.some((path) => location.pathname.startsWith(path));

  // All navigation items with role-based filtering
  const navItems = [
    { path: "/panel/orders", label: "Quotations", icon: ShoppingCart },
    { path: "/panel/quotation-status", label: "Quotation Status", icon: FileText },
    // Products dropdown (admin + employee/staff/user)
    ...(["admin", "employee", "staff", "user"].includes((role || "").toLowerCase())
      ? [{
          path: "/panel/products",
          label: "Products",
          icon: ShoppingCart,
          dropdown: [
            { path: "/panel/products/list", label: "Product" },
            { path: "/panel/brands", label: "Brands" },
          ],
        }]
      : []),
    // Customers tab after Products
    { path: "/panel/customers", label: "Customers", icon: Users },
    // Only show Users for admin
    ...(role === "admin" ? [{ path: "/panel/users", label: "Users", icon: Users }] : []),
    ...(role === "admin" ? [{ path: "/panel/signatories", label: "Signatories", icon: Users }] : []),
    ...(role === "admin" ? [{ path: "/panel/settings", label: "Settings", icon: Settings }] : []),
  ];

  // Mobile bottom bar - simplified to 4 items with role-based filtering
  const mobileBottomNavItems = [
    { path: "/panel/orders", label: "Quotations", icon: ShoppingCart },
    { path: "/panel/quotation-status", label: "Status", icon: FileText },
    { path: "/panel/customers", label: "Customers", icon: Users },
  ];

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/logout`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      // Clear Redux state
      dispatch(resetLoginData());
      
      // Redirect to login
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still log out the user even if API fails
      dispatch(resetLoginData());
      navigate('/login');
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/panel/orders');
  };

  return (
    <div className="flex flex-col md:flex-row min-h-0 h-[100dvh] max-h-[100dvh] w-full max-w-[100vw] overflow-hidden bg-white">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex md:flex-col bg-gradient-to-b from-[var(--sidebar-dark)] via-[var(--sidebar-primary)] to-[var(--sidebar-secondary)] text-[var(--sidebar-light)] transition-all duration-300 ${
          sidebarOpen ? "md:w-72" : "md:w-24"
        } shadow-2xl`}
      >
        {/* Logo/Header */}
        <div className="p-5 md:p-6 border-b border-white/10 bg-white/5 backdrop-blur-sm">
          <div
            className={`flex gap-3 ${sidebarOpen ? "flex-row items-center justify-between" : "flex-col items-center justify-center"}`}
          >
            {sidebarOpen ? (
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <SidebarBrandLogo size="md" />
                <div className="min-w-0">
                  <div className="font-bold text-2xl text-white tracking-tight">Cogent</div>
                  <div className="text-xs font-semibold text-slate-300 truncate" title={username}>
                    {username || "User"}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center gap-1 py-0.5" title="Cogent Admin Panel">
                <SidebarBrandLogo size="lg" />
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="shrink-0 p-2 text-slate-200 transition-all duration-200 hover:bg-white/10 rounded-lg group active:scale-95 hidden lg:block"
              title={sidebarOpen ? "Collapse" : "Expand"}
            >
              <Menu size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto relative z-10">
          <div className={`text-xs font-bold uppercase tracking-widest text-slate-300 px-4 mb-6 transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 hidden"}`}>
            <div className="flex items-center gap-2">
              <div className="w-1 h-3 bg-gradient-to-b from-[var(--accent-secondary)] to-[var(--accent-primary)] rounded-full"></div>
              Navigation
            </div>
          </div>
          {navItems.map((item) => {
            // Handle dropdown for Products with expand/collapse
            if (item.dropdown) {
              const isActiveDropdown = isDropdownActive([item.path, ...(item.dropdown?.map((sub) => sub.path) || [])]);
              return (
                <div key={item.path}>
                  <div
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl font-bold text-sm cursor-pointer transition-all duration-300 group relative overflow-hidden ${
                      isActiveDropdown
                        ? "bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white shadow-xl shadow-blue-900/30"
                        : "text-slate-200 hover:text-white hover:bg-white/10 hover:shadow-lg hover:shadow-blue-950/20"
                    }`}
                    onClick={() => setProductsDropdownOpen((open) => !open)}
                  >
                    {isActiveDropdown && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-blue-200/10 animate-pulse" />
                    )}
                    <div className={`relative z-10 transition-all ${isActiveDropdown ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                      <item.icon size={22} className={isActiveDropdown ? "drop-shadow-md" : ""} />
                    </div>
                    {sidebarOpen && (
                      <>
                        <span className="relative z-10 flex-1">{item.label}</span>
                        <svg
                          className={`relative z-10 w-4 h-4 transition-transform duration-300 ${productsDropdownOpen ? "rotate-180" : ""}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </div>
                  {sidebarOpen && productsDropdownOpen && (
                    <div className="ml-4 mt-1 mb-1 pl-4 border-l-2 border-white/10 space-y-0.5">
                      {item.dropdown.map((sub) => {
                        const subActive = isActive(sub.path);
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            onClick={() => setProductsDropdownOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 group/sub ${
                              subActive
                                ? "bg-white/10 text-white shadow-sm"
                                : "text-slate-300 hover:text-white hover:bg-white/10"
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all ${subActive ? "bg-[color:var(--accent-secondary)] scale-125" : "bg-slate-500 group-hover/sub:bg-[color:var(--accent-secondary)]"}`} />
                            {sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                  active
                    ? "bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white shadow-xl shadow-blue-900/30 transform scale-105"
                    : "text-slate-200 hover:text-white hover:bg-white/10 hover:shadow-lg hover:shadow-blue-950/20"
                }`}
              >
                {active && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-blue-200/10 transition-all animate-pulse" />
                )}
                <div className={`relative z-10 transition-all ${active ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                  {IconComponent && <IconComponent size={22} className={active ? "drop-shadow-md" : ""} />}
                </div>
                {sidebarOpen && (
                  <span className={`font-bold text-sm relative z-10 transition-all ${active ? "translate-x-0 drop-shadow" : "group-hover:translate-x-1"}`}>
                    {item.label}
                  </span>
                )}
                {active && !sidebarOpen && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-[var(--accent-secondary)] to-[var(--accent-primary)] rounded-full shadow-lg shadow-[color:var(--accent-secondary)]/50" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Logout Button */}
        <div className="border-t border-white/10 p-4 bg-black/20 backdrop-blur-sm space-y-3">
          <button
            onClick={handleLogout}
            disabled={logoutLoading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 bg-gradient-to-r from-red-600 to-red-700 text-white disabled:opacity-60 hover:shadow-lg hover:from-red-700 hover:to-red-800 ${
              sidebarOpen ? "" : "justify-center"
            } group active:scale-95 disabled:cursor-not-allowed`}
            style={sidebarOpen ? {
              boxShadow: '0 10px 20px rgba(185, 28, 28, 0.35)'
            } : {}}
            title={!sidebarOpen ? "Logout" : ""}
          >
            {logoutLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {sidebarOpen && <span>Logging out...</span>}
              </>
            ) : (
              <>
                <LogOut size={18} />
                {sidebarOpen && <span>Logout</span>}
              </>
            )}
          </button>
          
          <div className={`text-xs text-slate-400 text-center transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 hidden"}`}>
            <div className="font-semibold text-slate-200">Cogent</div>
            <div>© 2026</div>
          </div>
          {!sidebarOpen && (
            <div className="flex justify-center">
              <SidebarBrandLogo size="sm" />
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden pb-28 md:pb-0">
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-all text-gray-700 hover:text-gray-900 active:scale-95"
                title="Menu"
              >
                <Menu size={24} />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleGoBack}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 active:scale-95"
                    title="Go back"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <h1 className="text-2xl font-bold text-gray-800">{currentTitle}</h1>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* <div className="text-sm text-gray-600">
                Role: <span className="font-semibold capitalize text-emerald-600">{role || "User"}</span>
              </div> */}
              
              {/* Mobile Logout Button */}
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className="md:hidden p-2 hover:bg-red-50 rounded-lg transition-all text-red-600 hover:text-red-700 hover:scale-110 disabled:opacity-60 active:scale-95"
                title="Logout"
              >
                {logoutLoading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <LogOut size={20} />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto overflow-x-auto min-w-0 bg-white [overflow-wrap:anywhere]">
          <div className="p-3 sm:p-4 md:p-8 w-full min-w-0 max-w-full bg-white">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom bar: full pill container + pill active tab (no rectangular clips) */}
      <nav className="pointer-events-none fixed inset-x-0 bottom-0 z-40 md:hidden flex justify-center px-3 pb-4 pt-2">
        <div
          className="pointer-events-auto flex min-h-[5.25rem] w-full max-w-lg items-center justify-between gap-1.5 rounded-[9999px] border border-white/20 bg-gradient-to-r from-[var(--sidebar-dark)] via-[var(--sidebar-primary)] to-[var(--sidebar-secondary)] px-2.5 py-2 text-white shadow-2xl shadow-black/35"
          style={{ borderRadius: "9999px", borderTop: "2px solid var(--accent-secondary)" }}
        >
          {mobileBottomNavItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className="group flex min-h-0 min-w-0 flex-1 flex-col items-stretch justify-center"
              >
                <div
                  className={`flex min-h-[4rem] w-full min-w-[4.5rem] flex-col items-center justify-center gap-1 rounded-full px-2.5 py-2.5 transition-all duration-300 ${
                    active
                      ? "text-white scale-[1.02]"
                      : "text-[var(--sidebar-light)] hover:text-white hover:bg-white/12"
                  }`}
                  style={
                    active
                      ? {
                          background:
                            "linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))",
                          boxShadow: "0 8px 20px rgba(37, 99, 235, 0.4)",
                          borderRadius: 9999,
                        }
                      : { borderRadius: 9999 }
                  }
                >
                  <IconComponent
                    size={26}
                    className={active ? "drop-shadow-lg" : "opacity-95 group-hover:scale-110 transition-transform"}
                  />
                  <span
                    className={`px-0.5 text-center text-[11px] font-bold leading-snug tracking-tight ${active ? "drop-shadow-md" : ""}`}
                  >
                    {item.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile Menu Modal/Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 md:hidden z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute left-0 top-0 h-full w-64 bg-gradient-to-b from-[var(--sidebar-dark)] via-[var(--sidebar-primary)] to-[var(--sidebar-secondary)] text-white shadow-2xl animate-in slide-in-from-left flex flex-col">
            {/* Menu Header */}
            <div className="p-6 border-b border-white/10 bg-white/5 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <SidebarBrandLogo size="md" />
                <div className="min-w-0">
                  <div className="font-bold text-xl text-white tracking-tight">Cogent</div>
                  <div className="text-xs font-semibold text-slate-300 truncate" title={username}>
                    {username || "User"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="px-4 py-8 space-y-2 overflow-y-auto flex-1">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-300 px-4 mb-6">
                All Pages
              </div>
              {navItems.map((item) => {
                if (item.dropdown) {
                  const isActiveDropdown = isDropdownActive([item.path, ...(item.dropdown?.map((sub) => sub.path) || [])]);
                  return (
                    <div key={item.path}>
                      <div
                        className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden cursor-pointer ${
                          isActiveDropdown
                            ? "bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white shadow-xl shadow-blue-900/30"
                            : "text-slate-200 hover:text-white hover:bg-white/10"
                        }`}
                        onClick={() => setProductsDropdownOpen((open) => !open)}
                      >
                        {isActiveDropdown && (
                          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--accent-secondary)]/20 to-[color:var(--accent-primary)]/20 transition-all animate-pulse" />
                        )}
                        <div className={`relative z-10 transition-all ${isActiveDropdown ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                          <item.icon size={22} className={isActiveDropdown ? "drop-shadow-md" : ""} />
                        </div>
                        <span className="font-bold text-sm relative z-10 flex-1">{item.label}</span>
                        <svg
                          className={`relative z-10 w-4 h-4 transition-transform duration-300 ${productsDropdownOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {productsDropdownOpen && (
                        <div className="ml-4 mt-1 mb-1 pl-4 border-l-2 border-white/10 space-y-0.5">
                          {item.dropdown.map((sub) => {
                            const subActive = isActive(sub.path);
                            return (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                onClick={() => {
                                  setProductsDropdownOpen(false);
                                  setMobileMenuOpen(false);
                                }}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 group/sub ${
                                  subActive
                                    ? "bg-white/10 text-white shadow-sm"
                                    : "text-slate-300 hover:text-white hover:bg-white/10"
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all ${subActive ? "bg-[color:var(--accent-secondary)] scale-125" : "bg-slate-500 group-hover/sub:bg-[color:var(--accent-secondary)]"}`} />
                                {sub.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                const IconComponent = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                      active
                        ? "bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white shadow-xl shadow-blue-900/30"
                        : "text-slate-200 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    {active && (
                      <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--accent-secondary)]/20 to-[color:var(--accent-primary)]/20 group-hover:from-[color:var(--accent-secondary)]/30 group-hover:to-[color:var(--accent-primary)]/30 transition-all animate-pulse" />
                    )}
                    <div className={`relative z-10 transition-all ${active ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                      <IconComponent size={22} className={active ? "drop-shadow-md" : ""} />
                    </div>
                    <span className={`font-bold text-sm relative z-10 transition-all ${active ? "translate-x-0 drop-shadow" : "group-hover:translate-x-1"}`}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </nav>

            {/* Menu Footer - Logout Button */}
            <div className="border-t p-4 bg-black/20 space-y-3 mt-auto" style={{ borderTopColor: 'rgba(255, 255, 255, 0.1)' }}>
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 text-white disabled:opacity-60 group active:scale-95 disabled:cursor-not-allowed bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                style={{
                  boxShadow: '0 10px 20px rgba(185, 28, 28, 0.35)'
                }}
              >
                {logoutLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Logging out...</span>
                  </>
                ) : (
                  <>
                    <LogOut size={18} />
                    <span>Logout</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelPage;
